/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * useDivisionBudgets Hook
 *
 * Manages budgets associated with a division
 */

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import { QUERY_GC_TIME, QUERY_STALE_TIME } from "@/lib/constants/query-config";
import type { Database } from "@/lib/supabase/types";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";

// ============================================================================
// TYPES
// ============================================================================

type Budget = Database["public"]["Tables"]["budgets"]["Row"];

export interface DivisionBudget extends Budget {
  is_active_for_division?: boolean;
}

export interface DivisionBudgetsState {
  budgets: DivisionBudget[];
  activeBudget: DivisionBudget | null;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// HOOK
// ============================================================================

export function useDivisionBudgets(divisionId: string | null) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled, isOrganizationReady } =
    useOrganizationQueryGuard({
      enabled: !!divisionId,
      debugKey: "useDivisionBudgets",
    });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  // ============================================================================
  // FETCH BUDGETS FOR DIVISION
  // ============================================================================

  const budgetsQuery = useQuery<{
    budgets: DivisionBudget[];
    activeBudget: DivisionBudget | null;
  }>({
    queryKey: ["division-budgets", organizationId, divisionId],
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    meta: { entity: "division-budgets" },
    queryFn: async () => {
      // Fetch budgets filtered by division_id
      if (!organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }

      const res = await api.get<Budget[]>(
        `/api/budgets?division_id=${divisionId}`,
      );

      if (isApiFailure(res)) {
        throw new Error(res.message);
      }

      const budgets: DivisionBudget[] = Array.isArray(res.data) ? res.data : [];

      // Find active budget - look for status 'active' or 'published'
      const activeBudget =
        budgets.find(
          (b) => b.status === "active" || b.status === "published",
        ) ?? null;

      // Mark which budget is active
      const enrichedBudgets = budgets.map((b) => ({
        ...b,
        is_active_for_division: b.budget_id === activeBudget?.budget_id,
      }));

      return { budgets: enrichedBudgets, activeBudget };
    },
  });

  const budgetsState = useMemo<DivisionBudgetsState>(
    () => ({
      budgets: budgetsQuery.data?.budgets ?? [],
      activeBudget: budgetsQuery.data?.activeBudget ?? null,
      loading: budgetsQuery.isLoading || budgetsQuery.isFetching,
      error: budgetsQuery.error?.message ?? null,
    }),
    [
      budgetsQuery.data,
      budgetsQuery.isLoading,
      budgetsQuery.isFetching,
      budgetsQuery.error,
    ],
  );

  // ============================================================================
  // SET ACTIVE BUDGET
  // ============================================================================

  const setActiveBudgetMutation = useMutation({
    mutationFn: async (budgetId: string) => {
      // First, set other budgets in this division to non-active
      if (!organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }
      const currentBudgets = budgetsState.budgets;
      for (const budget of currentBudgets) {
        if (
          budget.budget_id !== budgetId &&
          (budget.status === "active" || budget.status === "published")
        ) {
          // Archive the old active budget
          await api.patch(`/api/budgets/${budget.budget_id}`, {
            status: "archived",
          });
        }
      }

      // Set the new budget as active
      const res = await api.patch<Budget>(`/api/budgets/${budgetId}`, {
        status: "active",
      });

      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to set active budget", res);
        throw new Error(res.message);
      }

      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["division-budgets", organizationId, divisionId],
      });
      // Also invalidate the general budgets list
      queryClient.invalidateQueries({
        queryKey: ["budgets", organizationId],
      });
    },
  });

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    ...budgetsState,
    isLoading: budgetsState.loading,
    hasError: !!budgetsState.error,

    // Actions
    setActiveBudget: setActiveBudgetMutation.mutateAsync,
    refetch: budgetsQuery.refetch,

    // Mutations (for direct access if needed)
    setActiveBudgetMutation,
  };
}
